// Shared by frame 01 sign-in and frame 03 link-expired — both are "type an
// email, POST /auth/request, go to /auth/link-sent" with different copy
// around it. One form, one submit/validate/error path, so the double-submit
// guard and the network-failure ErrorState only exist once.

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Button, Field, Input, ErrorState } from "../../ui";
import { isValidEmail, requestMagicLink } from "./authApi";

export type EmailRequestFormProps = {
  submitLabel: string;
  pendingLabel: string;
  help?: ReactNode;
  initialEmail?: string;
  onSuccess: (email: string) => void;
};

export function EmailRequestForm({
  submitLabel,
  pendingLabel,
  help,
  initialEmail = "",
  onSuccess,
}: EmailRequestFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function send(address: string) {
    setPending(true);
    setSubmitError(null);
    try {
      await requestMagicLink(address);
      onSuccess(address);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return; // native `disabled` already blocks this; belt and suspenders.
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setFieldError("Enter a valid email address.");
      return;
    }
    setFieldError(null);
    void send(trimmed);
  }

  if (submitError) {
    return (
      <ErrorState
        headline="Couldn't send the link"
        detail={submitError}
        action={
          <Button variant="primary" block onClick={() => void send(email.trim())}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <form className="maisight-signin-form" onSubmit={handleSubmit} noValidate>
      <Field label="Work email" help={help} error={fieldError ?? undefined}>
        <Input
          type="email"
          name="email"
          placeholder="lena@acme.com"
          value={email}
          disabled={pending}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Button type="submit" variant="primary" block disabled={pending}>
        {pending ? pendingLabel : submitLabel}
      </Button>
    </form>
  );
}
